import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function CommandRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [search, setSearch] = useState("")

  const children = element.children ?? []
  const searchInput = children.find((c) => c.type === "input")
  const groups = children.filter((c) => c.type !== "input")

  const placeholder = searchInput?.placeholder
    ? String(resolveSymbol(searchInput.placeholder) ?? "")
    : "Type a command or search..."

  const filterItems = (items: typeof children) =>
    items.filter((item) => {
      if (!search) return true
      const label = (item.text ?? "").toLowerCase()
      return label.includes(search.toLowerCase())
    })

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-popover shadow-md overflow-hidden",
        element.className
      )}
      data-testid={`command-${element.id}`}
      role="listbox"
      aria-label={element.ariaLabel ?? "Command palette"}
    >
      <div className="border-b border-border px-3 py-2">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            if (searchInput) {
              dispatchEvent(searchInput.id, "change", { value: e.target.value })
            }
          }}
          placeholder={placeholder}
          className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          aria-label={searchInput?.ariaLabel ?? "Search commands"}
        />
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {groups.map((group) => {
          const groupItems = group.children ?? []
          const filtered = filterItems(groupItems.filter((i) => i.type !== "text"))
          const label = groupItems.find((i) => i.type === "text")

          if (filtered.length === 0 && search) return null

          return (
            <div key={group.id} className="p-1">
              {label && (
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {String(resolveSymbol(label.text ?? "") ?? "")}
                </div>
              )}
              {filtered.map((item) => {
                const itemLabel = item.text
                  ? String(resolveSymbol(item.text) ?? "")
                  : item.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground transition-colors"
                    onClick={() =>
                      dispatchEvent(item.id, "tap", { value: itemLabel })
                    }
                    aria-label={item.ariaLabel ?? itemLabel}
                  >
                    {itemLabel}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
